import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Download, Printer } from 'lucide-react';
import { Header } from '../../Components/Header/Header';
import { RestockMachineCard } from '../../Components/Vending/RestockMachineCard';
import { RestockDialogLine } from '../../Components/Vending/VendingStyles';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import {
  VendingMappedCompany,
  VendingMappedLocation,
  VendingRestockQueueItem,
  VendingRestockQueueResponse,
} from '../../Types/vending';

const machineDisplayName = (item: VendingRestockQueueItem) =>
  item.machine_name || `VM-${item.provider_machine_id}`;

const locationDisplay = (item: VendingRestockQueueItem) => {
  if (item.location_name) return item.location_name;
  const parts = [item.city, item.state].filter(Boolean);
  if (parts.length) return parts.join(', ');
  return 'Unassigned';
};

export const VendingRestockPage = () => {
  const [searchParams] = useSearchParams();
  const highlightMachineId = searchParams.get('machineId');

  const [items, setItems] = useState<VendingRestockQueueItem[]>([]);
  const [companies, setCompanies] = useState<VendingMappedCompany[]>([]);
  const [locations, setLocations] = useState<VendingMappedLocation[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyFilter, setCompanyFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [completingId, setCompletingId] = useState<number | null>(null);
  const [pickListOpen, setPickListOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setLocationFilter('');
  }, [companyFilter]);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ limit: '200' });
      if (companyFilter) params.set('cid', companyFilter);
      if (locationFilter) params.set('ovid', locationFilter);
      if (priorityFilter) params.set('priority', priorityFilter);

      const response = await vendingApiClient(`/restock?${params.toString()}`);
      const json: VendingRestockQueueResponse = await response.json();
      setItems(json.data);
      setCompanies(json.meta?.companies ?? []);
      setLocations(json.meta?.locations ?? []);
      setAsOf(json.meta?.as_of ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load restock queue');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [companyFilter, locationFilter, priorityFilter]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    if (!highlightMachineId || loading) return;
    const el = document.getElementById(`restock-machine-${highlightMachineId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightMachineId, loading, items]);

  const handleComplete = async (machineId: number) => {
    setCompletingId(machineId);
    setActionMessage(null);
    try {
      await vendingApiClient(`/restock/${machineId}/complete`, { method: 'POST' });
      setActionMessage({ text: 'Restock marked complete.', severity: 'success' });
      await loadQueue();
    } catch (err) {
      setActionMessage({
        text: err instanceof Error ? err.message : 'Failed to mark restock complete',
        severity: 'error',
      });
    } finally {
      setCompletingId(null);
    }
  };

  const consolidatedLines = useMemo(
    () => items.flatMap((item) => item.lines.map((line) => ({
      key: `${item.machine_id}-${line.slot_id}`,
      machineName: machineDisplayName(item),
      locationName: locationDisplay(item),
      sku: line.product?.provider_sku || '—',
      productName: line.product?.name || 'Unknown product',
      qty: line.qty_to_bring,
    }))),
    [items],
  );

  const exportPickListCsv = () => {
    const headers = ['Machine', 'Location', 'SKU', 'Product', 'Qty'];
    const rows = consolidatedLines.map((line) => [
      line.machineName,
      line.locationName,
      line.sku,
      line.productName,
      line.qty,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vending-restock-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  const clearFilters = () => {
    setCompanyFilter('');
    setLocationFilter('');
    setPriorityFilter('');
  };

  const hasActiveFilters = Boolean(companyFilter || locationFilter || priorityFilter);

  return (
    <PageContainer>
      <Header
        title="Restock"
        subtitle={
          asOf
            ? `${items.length} machine${items.length === 1 ? '' : 's'} need attention • Data as of ${formatUTC(asOf)} UTC`
            : 'Machines needing replenishment'
        }
        count={items.length}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {items.length > 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                onClick={() => setPickListOpen(true)}
              >
                Pick List
              </Button>
              <Button variant="outlined" startIcon={<Download size={16} />} onClick={exportPickListCsv}>
                Export CSV
              </Button>
            </>
          )}
          <Button variant="outlined" onClick={loadQueue} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Header>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: { xs: 2, md: 3 }, pb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            py: 2,
            flexShrink: 0,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              label="Customer"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <MenuItem value="">All customers</MenuItem>
              {companies.map((company) => (
                <MenuItem key={company.cid} value={String(company.cid)}>
                  {company.customer_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }} disabled={!companyFilter}>
            <InputLabel>Location</InputLabel>
            <Select
              label="Location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <MenuItem value="">All locations</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.ovid} value={String(loc.ovid)}>
                  {loc.location_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              label="Severity"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button variant="text" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </Box>

        {actionMessage && (
          <Alert severity={actionMessage.severity} sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
            {actionMessage.text}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={loadQueue}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!loading && !error && items.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 2,
              borderRadius: 2,
              border: `1px dashed ${COLORS.lightGray}`,
              backgroundColor: '#fafbfc',
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
              No replenishment required
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
              No machines currently require replenishment.
            </Typography>
          </Box>
        )}

        {!loading && !error && items.length > 0 && (
          <Box id="restock-print-area">
            {items.map((item) => (
              <RestockMachineCard
                key={item.machine_id}
                item={item}
                highlighted={highlightMachineId === String(item.machine_id)}
                completing={completingId === item.machine_id}
                onComplete={handleComplete}
              />
            ))}
          </Box>
        )}
      </Box>

      <Dialog open={pickListOpen} onClose={() => setPickListOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
          Today&apos;s Vending Restock
        </DialogTitle>
        <DialogContent>
          {consolidatedLines.length === 0 ? (
            <Typography sx={{ py: 2, color: COLORS.lightGray, textAlign: 'center' }}>
              No products to pick.
            </Typography>
          ) : (
            <>
              {items.map((item) => (
                <Box key={item.machine_id} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: COLORS.teal }}>
                    Machine {machineDisplayName(item)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: COLORS.lightGray, mb: 1 }}>
                    {locationDisplay(item)}
                  </Typography>
                  {item.lines.map((line) => (
                    <RestockDialogLine key={line.slot_id}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr 60px', gap: 1, width: '100%' }}>
                        <Typography variant="body2" sx={{ color: COLORS.lightGray, fontFamily: 'monospace' }}>
                          {line.product?.provider_sku || '—'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {line.product?.name || 'Unknown'}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.teal, textAlign: 'right' }}>
                          {line.qty_to_bring}
                        </Typography>
                      </Box>
                    </RestockDialogLine>
                  ))}
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                <Button variant="outlined" startIcon={<Printer size={16} />} onClick={handlePrint}>
                  Print
                </Button>
                <Button variant="contained" startIcon={<Download size={16} />} onClick={exportPickListCsv}>
                  Export CSV
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default VendingRestockPage;
